import React, { useMemo } from "react";
import {
  InlineTypeahead,
  InlineTypeaheadProps,
} from "~/components/inline-typeahead";

export type EnumType = {
  name: string;
  description: string;
};

const format = (item: EnumType) =>
  item?.description?.length > 0 ? item.description : item?.name;

export type InlineEnumPickerProps<E extends string> = {
  value: EnumType | null;
  name: string;
  onChange: (value: EnumType | null) => void;
  onBlur?: React.FocusEventHandler<any>;
  labels: Record<E, string>;
  required?: boolean;
  submitError?: string;
  excluded?: string[];
};

export function InlineEnumPicker<E extends string>(
  props: InlineEnumPickerProps<E>
): ReturnType<React.FC> {
  const { labels, value, excluded, ...rest } = props;

  const allValues = useMemo(
    () =>
      Object.entries<string>(labels).map(
        ([name, description]): EnumType => ({ name, description })
      ),
    [labels]
  );
  const options = allValues.filter(
    (x) => !excluded || !excluded.includes(x.name)
  );

  const initialValue = value
    ? options.find((x) => x.name === value.name) ?? null
    : null;

  const getData = async (searchText: string) => {
    if (!searchText) {
      return options;
    }
    const lcText = searchText.toLowerCase();

    // Users type what they see, the label, but the value name still matches.
    return options.filter(
      (x) =>
        x.name.toLowerCase().startsWith(lcText) ||
        x.description.toLowerCase().startsWith(lcText)
    );
  };

  const args = {
    ...rest,
    value: initialValue,
    getData,
    format,
  } as InlineTypeaheadProps<EnumType>;

  return <InlineTypeahead {...args} />;
}
